import React, { useState, useEffect } from 'react'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
// eslint-disable-next-line
import { Button, Modal, Tabs, Space, Switch } from 'antd'
// eslint-disable-next-line
import {
  successNotification,
  errorNotification,
  carImagesUrl,
  getRequest,
  carsUrl,
  postRequest
} from '../../config'
import CarImageForm from '../CarImages/Form'
import BulkUploadForm from '../CarImages/BulkUploadForm'
import CarImageUpdate from '../CarImages/Update'
import CropImage from '../CarImages/CropImage'
import EditImage from '../CarImages/EditImage'
import FilterTable from '../../components/Forms/searchFilter'
import PageLoader from '../../components/Layouts/PageLoader'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min'
import { imageColumns } from './tableColumns'
import ImageCards from './ImageCards'
import usePersistedState from '../../hooks/usePersistedState'
import LightBox from 'components/LightBox'

const { TabPane } = Tabs

const CarTableSection = props => {
  const car = props.car
  // eslint-disable-next-line
  const [serverErrors, setServerErrors] = useState({})
  const [loading, setLoading] = useState(false)
  // image state
  const [showImageFormModal, setShowImageFormModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false)
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [showImageEditModal, setShowImageEditModal] = useState(false)
  const [openSlideShow, setOpenSlideShow] = useState(false)
  const [images, setImages] = useState([])
  const [currentImage, setCurrentImage] = useState({})
  const [imageModalKey, setImageModalKey] = useState(0)
  // Saved like the dark-mode setting: flip it once on the phone and it stays flipped.
  const [mobileView, setMobileView] = usePersistedState(
    'wabenzy-car-images-mobile-view',
    false
  )
  const [slideShowStart, setSlideShowStart] = useState(0)

  // The card list hands back the image; the table's columns set it themselves. Both end at the
  // same two lines, so the modals below do not care which view opened them.
  const openWith = setShow => image => {
    setCurrentImage(image)
    setShow(true)
  }

  const getCarImages = async () => {
    const id_ = props.match.params.id
    const response = await getRequest(
      `${carImagesUrl}/for-car/?car=${id_}`,
      'car images',
      setLoading
    )
    if (response.success) {
      setImages(response.data)
    } else {
      if (response.error.response) {
        errorNotification(response.error.response.data.message)
      }
    }
  }

  const handleDeleteImage = async () => {
    const response = await postRequest(
      carsUrl + '/delete-image',
      { imageId: currentImage.id },
      'delete image',
      setLoading
    )
    if (response.success) {
      successNotification('Image Deleted')
      setImages(images.filter(image => image.id !== currentImage.id))
      setCurrentImage({})
      setShowDeleteImageModal(false)
    } else {
      if (response.error.response) {
        errorNotification(response.error.response.data.message)
      }
    }
  }

  useEffect(() => {
    getCarImages()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (
      showImageFormModal ||
      showImageUpdateModal ||
      showBulkUploadModal ||
      showImageCropModal
    ) {
      setImageModalKey(imageModalKey + 1)
    }
    // eslint-disable-next-line
  }, [
    showImageFormModal,
    showImageUpdateModal,
    showBulkUploadModal,
    showImageCropModal,
    showImageEditModal
  ])

  const handleDownloadAllImages = async () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    if (isMobile) {
      images.forEach((img, index) => {
        setTimeout(() => {
          const link = document.createElement('a')
          link.href = img.image
          link.download =
            img.image.split('/').pop() ||
            `image-${car.year}-${car.make.name}-${car.model.name}-${car.trim}-${index}.jpg`
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }, index * 800)
      })
    } else {
      setLoading(true)
      const zip = new JSZip()
      try {
        const promises = images.map(async (img, index) => {
          const response = await fetch(img.image)
          if (!response.ok) throw new Error('Network response was not ok')
          const blob = await response.blob()
          const filename = img.image.split('/').pop() || `image-${index}.jpg`
          zip.file(filename, blob)
        })

        await Promise.all(promises)
        const content = await zip.generateAsync({ type: 'blob' })
        saveAs(
          content,
          `car-images-${car.year}-${car.make.name}-${car.model.name}-${car.trim}.zip`
        )
        successNotification('Images downloaded successfully')
      } catch (error) {
        console.error('Error downloading images: ', error)
        errorNotification('Failed to download images, try again.')
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <PageLoader className="container" loading={loading}>
      <Tabs defaultActiveKey="1" onChange={() => {}}>
        <TabPane tab="Car Images" key="3">
          <div className="card mb-5rem">
            <div className="card-body">
              <div
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Space wrap>
                  <Button
                    type="primary"
                    onClick={() => setShowImageFormModal(true)}
                  >
                    Add New Image
                  </Button>
                  <Button onClick={() => setShowBulkUploadModal(true)}>
                    Bulk Upload
                  </Button>
                  <Button
                    type="success"
                    onClick={() => {
                      setSlideShowStart(0)
                      setOpenSlideShow(true)
                    }}
                  >
                    View all
                  </Button>
                  <Button type="success" onClick={handleDownloadAllImages}>
                    Dowload all images
                  </Button>
                </Space>
                <Space size="small">
                  <span>Mobile view</span>
                  <Switch
                    checked={mobileView}
                    onChange={setMobileView}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                  />
                </Space>
              </div>

              {mobileView ? (
                <ImageCards
                  images={images}
                  onUpdate={openWith(setShowImageUpdateModal)}
                  onCrop={openWith(setShowImageCropModal)}
                  onEdit={openWith(setShowImageEditModal)}
                  onDelete={openWith(setShowDeleteImageModal)}
                  onPreview={index => {
                    setSlideShowStart(index)
                    setOpenSlideShow(true)
                  }}
                />
              ) : (
                <FilterTable
                  dataSource={images}
                  columns={imageColumns(
                    setCurrentImage,
                    setShowDeleteImageModal,
                    setShowImageUpdateModal,
                    setShowImageCropModal,
                    setShowImageEditModal
                  )}
                />
              )}
            </div>
          </div>
        </TabPane>
      </Tabs>

      <LightBox
        images={images.map(img => img.image)}
        openSlideShow={openSlideShow}
        setOpenSlideShow={setOpenSlideShow}
        index={slideShowStart}
      />

      <Modal
        title="Add New Image"
        centered
        open={showImageFormModal}
        onOk={() => setShowImageFormModal(false)}
        onCancel={() => setShowImageFormModal(false)}
        footer={null}
        width={1000}
      >
        <CarImageForm
          key={imageModalKey}
          car={car}
          handleCancel={() => {
            setShowImageFormModal(false)
            getCarImages()
          }}
        />
      </Modal>

      <Modal
        title="Bulk Upload Images"
        centered
        open={showBulkUploadModal}
        onOk={() => setShowBulkUploadModal(false)}
        onCancel={() => setShowBulkUploadModal(false)}
        footer={null}
        width={1000}
      >
        <BulkUploadForm
          key={imageModalKey}
          car={car}
          handleCancel={() => {
            setShowBulkUploadModal(false)
            getCarImages()
          }}
        />
      </Modal>

      <Modal
        title="Update Image"
        centered
        open={showImageUpdateModal}
        onOk={() => setShowImageUpdateModal(false)}
        onCancel={() => setShowImageUpdateModal(false)}
        footer={null}
        width={1000}
      >
        <CarImageUpdate
          key={imageModalKey}
          currentImage={currentImage}
          handleCancel={data => {
            setImages(images.map(img => (img.id === data.id ? data : img)))
            setShowImageUpdateModal(false)
          }}
        />
      </Modal>

      <Modal
        title="Crop Image"
        centered
        open={showImageCropModal}
        onOk={() => setShowImageCropModal(false)}
        onCancel={() => setShowImageCropModal(false)}
        footer={null}
        width={800}
      >
        <CropImage
          key={imageModalKey}
          currentImage={currentImage}
          handleSuccess={data => {
            setImages(images.map(img => (img.id === data.id ? data : img)))
            setShowImageCropModal(false)
          }}
        />
      </Modal>

      <Modal
        title="Edit Image"
        centered
        open={showImageEditModal}
        onOk={() => setShowImageEditModal(false)}
        onCancel={() => setShowImageEditModal(false)}
        footer={null}
        width={1000}
      >
        <EditImage
          key={imageModalKey}
          currentImage={currentImage}
          handleSuccess={data => {
            setImages(images.map(img => (img.id === data.id ? data : img)))
            setShowImageEditModal(false)
          }}
        />
      </Modal>

      <Modal
        title="Do You Really Want to Delete This Image?"
        centered
        open={showDeleteImageModal}
        onOk={() => setShowDeleteImageModal(false)}
        onCancel={() => setShowDeleteImageModal(false)}
        footer={null}
      >
        <div
          className="p-3"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <span className="text-danger mb-3">Go ahead and delete image?</span>
          <Button
            type="danger"
            onClick={() => {
              handleDeleteImage()
            }}
            loading={loading}
          >
            Delete Image
          </Button>
        </div>
      </Modal>
    </PageLoader>
  )
}

CarTableSection.propTypes = {
  car: PropTypes.object,
  handleCalback: PropTypes.func,
  // From withRouter: the car's id comes from the route, not from the `car` prop.
  match: PropTypes.object
}

export default withRouter(CarTableSection)
